import { INPUT_TYPES, REGEX_PATTERNS, TEXT_AREA } from '@/app.constants'
import { cn } from '@/utils'
import { AlertTriangle, Eye, EyeOff, Info } from 'lucide-react'
import React, { ChangeEvent, useEffect, useState } from 'react'
import { v7 as uuidv7 } from 'uuid'
import CustomTooltip from '../tooltip'
import { useMinMaxValidations, useRestriction, useTrim } from './hooks'
import useHtmlValidations from './hooks/useHtmlValidation'
import { ErrorIcon, InfoIcon, RequiredIcon, SuccessIcon } from '@/assets/icons'

const INFO_TYPES = {
  INFO: 'info',
  WARNING: 'warning',
}

interface iInputFieldProps {
  label: string;
  isRequired?: boolean;
  errors?: any;
  name?: string;
  infoText?: string;
  infoType?: string;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  isSuccess?: boolean;
  isClearable?: boolean;
  isTooltipError?: boolean;
  disabled?: boolean;
  width?: string;
  tooltipId?: string;
  type?: string;
  tooltipText?: string;
  tooltipPlacement?: string;
  tooltipClassName?: string;
  hideErrorText?: boolean;
  disabledErrorIcon?: boolean;
  placeholder?: string;
  minMaxConfig?: {
    min: number;
    max: number;
    toastConfig: any;
  };
  trimConfig?: {
    trimValue: boolean;
    toastConfig: any;
  };
  restrictions?: any;
  onChange?: (e: any) => void;
  onBlur?: (e: any) => void;
  onInput?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
  ref?: React.Ref<HTMLInputElement>;
  value?: string;
  id?: string;
  onWheel?: (e: React.WheelEvent<any>) => void;
  variant?: 'standard' | 'minimal';
  step?: string | number;
}

const InputField = React.forwardRef<HTMLInputElement, iInputFieldProps>(
  (
    {
      label,
      isRequired = false,
      errors = null,
      name,
      infoText = '',
      infoType = '',
      startAdornment = null,
      endAdornment = null,
      isSuccess = false,
      isClearable = false,
      isTooltipError = false,
      disabled = false,
      width = '100%',
      tooltipId,
      type = '',
      tooltipText,
      tooltipPlacement,
      tooltipClassName,
      hideErrorText = false,
      disabledErrorIcon = false,
      placeholder = '',
      minMaxConfig = {
        min: undefined,
        max: undefined,
        toastConfig: undefined,
      },
      trimConfig = {
        trimValue: true,
        toastConfig: undefined,
      },
      readOnly = false,
      restrictions,
      variant,
      ...props
    },
    ref,
  ): React.ReactElement => {

    const id = React.useRef(uuidv7())
    const [inputType, setInputType] = useState(type)
    const [isFocused, setIsFocused] = useState(false)

    const handleToggleShowPassword = (type: string) => {
      setInputType(type)
    }

    // Use the HTML validation hook with the internal ref
    const { handleHtmlValidation } = useHtmlValidations({
      type,
      onChange: props.onChange as unknown as (e: FocusEvent) => void,
    })

    useEffect(() => {
      if (type) {
        setInputType(type)
      }
    }, [type])

    const { handleOnBlur: handleOnBlurMinMax } = useMinMaxValidations({
      min: minMaxConfig.min,
      max: minMaxConfig.max,
      toastConfig: minMaxConfig.toastConfig,
      onChange: props.onChange,
    })

    const { handleOnBlur: handleOnBlurTrim } = useTrim({
      trimValue: trimConfig.trimValue,
      toastConfig: trimConfig.toastConfig,
      onChange: props.onChange,
    })

    const { handleOnInput: handleOnInputRestriction } = useRestriction({
      restrictions: restrictions,
    })

    // Label color based on the state of the input
    const labelColor = cn({
      'text-dark': disabled || readOnly, // Disabled
      'text-danger': !disabled && errors, // Error (only if not disabled)
      'text-success': !disabled && !errors && isSuccess, // Success
      'peer-focus:!text-primary': !disabled && !errors && !isSuccess, // Focus
      'peer-[&:not(:placeholder-shown)]:text-dark': !disabled && !errors && !isSuccess, // Not placeholder shown
      'text-dark_4': !disabled && !errors && !isSuccess, // Default
    })

    const getInfoIcon = () => {
      switch (infoType) {
        case INFO_TYPES.INFO:
          return <Info size={12} strokeWidth={1.5} color='var(--primary)' />
        case INFO_TYPES.WARNING:
          return <AlertTriangle size={12} color='var(--warning)' />
        default:
          return <InfoIcon />
      }
    }

    const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      handleOnBlurMinMax(e as unknown as FocusEvent)

      //implement only on text & text-area type
      if (trimConfig.trimValue && (type === '' || type === TEXT_AREA)) {
        handleOnBlurTrim(e as unknown as FocusEvent)
      }
      handleHtmlValidation(e as unknown as FocusEvent)
      if (props.onBlur) {
        props.onBlur(e as unknown as React.FocusEvent<HTMLInputElement>)
      }
    }

    const onInput = (e: React.ChangeEvent<HTMLInputElement>) => {
      handleOnInputRestriction(e)
      if (props.onInput) {
        props.onInput(e as unknown as ChangeEvent<HTMLInputElement>)
      }
    }

    return (
      <div className='omniful-floating-input h-fit m-0 box-border'>
        <div className='flex items-center gap-1'>
          <div
            className={cn('omniful-floating-input-wrapper relative flex items-center !gap-[8px] w-[300px] !p-0 transition duration-200 ease-in-out outline-none focus-within:border-primary !h-fit', {
              'border border-solid border-dark_2 rounded-[8px] bg-white !pl-[12px] !pr-[12px]': variant === 'standard' || !variant,
              'invalid-input !border-danger': errors && !disabled && (variant === 'standard' || !variant),
              'success-state !border-success': isSuccess && (variant === 'standard' || !variant),
              'disabled-state !bg-light_4 !border-dark_2': (disabled || readOnly) && (variant === 'standard' || !variant),
              'textarea-wrapper': type === 'textarea',
              '!bg-transparent !p-0': variant === 'minimal',
            })}
            style={{ width }}
          >
            {startAdornment}
            {type === 'textarea' ? (
              <textarea
                id={id.current}
                name={name}
                className={cn('peer text-[12px] w-full h-full outline-none !p-0 !bg-transparent !py-[12px] !border-none [&::placeholder]:!text-dark_5 [&::placeholder]:!text-[12px] [&::placeholder]:!font-normal [&::placeholder]:!leading-[18px] transition duration-200 ease-in-out !text-[12px] !min-h-[60px]', {'resize-none': disabled})}
                placeholder={!!label && !isFocused ? ' ' : placeholder}
                onFocus={() => setIsFocused(true)}
                onBlurCapture={() => setIsFocused(false)}
                disabled={disabled}
                {...props}
                onBlur={onBlur as unknown as React.FocusEventHandler<HTMLTextAreaElement>}
                onInput={onInput as unknown as React.FormEventHandler<HTMLTextAreaElement>}
                ref={ref as React.Ref<HTMLTextAreaElement>}
              />
            ) : (
              <input
                id={id.current}
                type={isClearable ? 'search' : inputType}
                autoComplete='new-password'
                className={cn('peer text-[12px] w-full h-full outline-none p-0 bg-transparent py-[12px] border-none [&::placeholder]:text-dark_5 [&::placeholder]:text-[12px] [&::placeholder]:font-normal [&::placeholder]:leading-[18px] transition duration-200 ease-in-out', {'is-required': isRequired })}
                placeholder={!!label && !isFocused ? ' ' : placeholder}
                onFocus={() => setIsFocused(true)}
                onBlurCapture={() => setIsFocused(false)}
                name={name}
                disabled={disabled}
                onKeyPress={(e) => {
                  // Allow only numeric input
                  if (type === INPUT_TYPES.NUMBER && !REGEX_PATTERNS.NUMBER.test(e.key)) {
                    e.preventDefault()
                  }
                }}
                {...props}
                onBlur={onBlur}
                onInput={onInput}
                readOnly={readOnly}
                ref={ref} // Pass the ref to the input element
              />
            )}
            <label htmlFor={id.current}
              className={cn('absolute px-[4px] top-1/2 left-10px text-[12px] line-height-[18px] -translate-y-1/2 flex pointer-events-none transition-0.2s ease-in-out peer-focus:!top-0 peer-disabled:!opacity-100 peer-[&:not(:placeholder-shown)]:!top-0 peer-not(:placeholder-shown):!line-height-[12px] peer-not(:placeholder-shown):!visible peer-focus:[background:linear-gradient(to_bottom,transparent_43%,var(--white)_40%,var(--white)_55%,transparent_55%)] peer-[&:not(:placeholder-shown)]:[background:linear-gradient(to_bottom,transparent_43%,var(--white)_40%,var(--white)_55%,transparent_55%)] peer-disabled:[background:linear-gradient(to_bottom,transparent_43%,var(--light_4)_40%,var(--light_4)_55%,transparent_55%)]',
                type === TEXT_AREA && 'textarea-label top-[24px]',
                !!startAdornment && 'label-with-startAdornment left-[38px]',
                labelColor,
              )}>
              {label}
              {isRequired && (
                <span className='required-icon contents peer-[&:not(:placeholder-shown)]:!visible peer-[&:is(:placeholder-shown)]:!hidden'>
                  {' '}
                  <RequiredIcon />{' '}
                </span>
              )}
            </label>
            {type === INPUT_TYPES.PASSWORD && (
              <span className='cursor-pointer'>
                {type === INPUT_TYPES.PASSWORD ? (
                  inputType === INPUT_TYPES.PASSWORD ? (
                    <EyeOff size={16} onClick={() => handleToggleShowPassword(INPUT_TYPES.TEXT)} color='var(--dark_5)'/>
                  ) : (
                    <Eye size={16} onClick={() => handleToggleShowPassword(INPUT_TYPES.PASSWORD)} color='var(--dark_5)'/>
                  )
                ) : null}
              </span>
            )}
            {endAdornment && (isClearable || type === INPUT_TYPES.PASSWORD) && <span className='vertical-divider h-[20px] border-0.5 border-solid border-dark_2'></span>}
            {endAdornment}
            {name && isTooltipError && errors?.[name] && !disabled && (
              <CustomTooltip
                placement='top'
                className='bg-danger border-danger error-text text-white text-[12px] leading-[16px] pt-[3px] font-normal'
                flip={false}
                isVisible={name && isTooltipError && errors && !disabled}
                content={ errors}
              >
                <span id='__errror_icon_input' className={cn('error-icon h-fit hidden', { 'icon-visible inline': isTooltipError && errors && !disabled })}>
                  {!disabledErrorIcon && <ErrorIcon />}
                </span>
              </CustomTooltip>
            )}

            {isSuccess && (
              <span className='success-icon'>
                {' '}
                <SuccessIcon />{' '}
              </span>
            )}
          </div>
        </div>

        {errors && !disabled && !isTooltipError && !hideErrorText && (
          <div className='flex items-center error-state mt-[2px]'>
            <span className='info-icon ml-[12px] mr-[8px]'> {!disabledErrorIcon && <ErrorIcon />} </span>
            <span className='error-text text-danger text-[12px] leading-[16px] pt-[3px] font-normal'>{errors}</span>
          </div>
        )}

        {!errors && !disabled && infoText && (
          <div className='pt-1 flex items-center gap-1'>
            <span className='ml-3'>
              {' '}
              {getInfoIcon()}{' '}
            </span>
            <span className='text-dark_5 text-[11px] leading-4'>{infoText}</span>
          </div>
        )}
      </div>
    )
  },
)

export default InputField
